(() => {
  const CART_EVENTS = [
    'on:cart:after-merge',
    'on:cart:change',
    'on:cart:add',
    'on:cart-drawer:after-open',
    'dispatch:cart-drawer:refresh'
  ];
  const SEARCH_EVENTS = [
    'theme:search:results-updated'
  ];
  const FREE_SHIPPING_PRICE_SELECTOR = [
    '[class*="free_shipping_card_"]',
    '[class*="free_shipping_card_product_price_"]',
    '[class*="free_shipping_card_title_"]',
    '[class*="essential_product"]',
    '.order-value-booster-side-cart',
    '.free-shipping-bar__message'
  ].join(',');
  const CART_PRICE_SELECTOR = [
    'cart-form .theme-money',
    'cart-form .money',
    '.cart-drawer .theme-money',
    '.cart-drawer .money',
    '.cart-item-upsells .theme-money',
    '.cart-item-upsells .money',
    'cc-cart-cross-sell .theme-money',
    'cc-cart-cross-sell .money',
    '.template-cart .theme-money',
    '.template-cart .money',
    '.main-search__results .theme-money',
    '.main-search__results .money',
    '.main-search__results .price__current',
    '.main-search__results .price__was',
    '.main-search__results .unit-price__price',
    '#glFilter-search-popup .money',
    '#glFilter-search-popup .gl-card-price',
    '#glFilter-search-popup .gl-card-old-price',
    '#glFilter-search-popup .gl-card-pricing .money',
    '#glFilter-search-popup .gl-fullscreen-results__content .money'
  ].join(',');
  const WATCH_SELECTOR = [
    'cart-form',
    'cart-drawer',
    'cc-cart-cross-sell',
    '.cart-item-upsells',
    '.order-value-booster-side-cart',
    '[class*="free_shipping_card_"]',
    '[class*="essential_product"]',
    '.free-shipping-bar',
    '.quickbuy-container',
    '.main-search',
    '.main-search__results',
    '.main-search__results__products',
    '.main-search-result',
    '#glFilter-search-popup',
    '.gl-fullscreen-suggestions',
    '.gl-fullscreen-initial-content',
    '.gl-fullscreen-results__content',
    '.gl-card',
    '.gl-card-pricing'
  ].join(',');
  const PRICE_AMOUNT_PATTERN = String.raw`\d(?:[\d\s,.]*\d)?`;
  const PRICE_TEXT_PATTERN = new RegExp(
    String.raw`(^|[\s(])((?:R|ZAR)\s?${PRICE_AMOUNT_PATTERN}|\$\s?${PRICE_AMOUNT_PATTERN}|(?:USD|EUR|GBP|AUD|AED)\s?${PRICE_AMOUNT_PATTERN})`,
    'g'
  );

  let scheduledRun;
  let observer;
  let observerStarted = false;
  let lastRunUsedRerender = false;
  let lastDomRefresh = 0;
  let bucksReadyPolls = 0;

  const hasBucks = () => Boolean(
    window.bucksCC
    || document.querySelector('#bucksSelector, .buckscc-currency-wrapper')
  );

  const safeCall = (fn) => {
    try {
      fn();
    } catch (error) {
      // BUCKS is third-party code; never let converter failures block cart behavior.
    }
  };

  const isInsideBucksMarkup = (node) => (
    node.parentElement
    && node.parentElement.closest('.money, .buckscc-money, .buckscc-currency-wrapper, .buckscc-floating-box')
  );

  const wrapTextNodePrices = (textNode) => {
    PRICE_TEXT_PATTERN.lastIndex = 0;

    if (!textNode.nodeValue || !PRICE_TEXT_PATTERN.test(textNode.nodeValue)) {
      PRICE_TEXT_PATTERN.lastIndex = 0;
      return false;
    }

    PRICE_TEXT_PATTERN.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let didWrap = false;
    let match = PRICE_TEXT_PATTERN.exec(textNode.nodeValue);

    while (match) {
      const prefix = match[1] || '';
      const priceText = match[2];
      const priceIndex = match.index + prefix.length;

      if (priceIndex > lastIndex) {
        fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex, priceIndex)));
      }

      const price = document.createElement('span');
      price.className = 'money';
      price.textContent = priceText;
      fragment.appendChild(price);
      didWrap = true;
      lastIndex = priceIndex + priceText.length;
      match = PRICE_TEXT_PATTERN.exec(textNode.nodeValue);
    }

    if (lastIndex < textNode.nodeValue.length) {
      fragment.appendChild(document.createTextNode(textNode.nodeValue.slice(lastIndex)));
    }

    textNode.parentNode.replaceChild(fragment, textNode);
    return didWrap;
  };

  const wrapFreeShippingPrices = () => {
    document.querySelectorAll(FREE_SHIPPING_PRICE_SELECTOR).forEach((container) => {
      if (container.closest('.buckscc-currency-wrapper, .buckscc-floating-box')) return;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (isInsideBucksMarkup(node)) return NodeFilter.FILTER_REJECT;
          PRICE_TEXT_PATTERN.lastIndex = 0;
          return PRICE_TEXT_PATTERN.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
        }
      });

      const textNodes = [];
      let node = walker.nextNode();
      while (node) {
        textNodes.push(node);
        node = walker.nextNode();
      }

      textNodes.forEach(wrapTextNodePrices);
    });
  };

  const markCartMoneyForConversion = () => {
    document.querySelectorAll(CART_PRICE_SELECTOR).forEach((el) => {
      if (el.closest('.buckscc-currency-wrapper, .buckscc-floating-box')) return;
      if (el.matches('.buckscc-money') || el.querySelector('.buckscc-money')) return;

      if (el.matches('.money')) {
        el.classList.add('money');
        return;
      }

      PRICE_TEXT_PATTERN.lastIndex = 0;
      if (el.firstChild && el.children.length === 0 && PRICE_TEXT_PATTERN.test(el.textContent || '')) {
        wrapTextNodePrices(el.firstChild);
      }
    });
  };

  const triggerBucksDomRefresh = () => {
    const now = Date.now();
    if (now - lastDomRefresh < 1200) return;

    const selectedCurrency = (
      document.querySelector('#bucksSelector')?.value
      || document.querySelector('.bucks-selected')?.textContent?.trim()
      || ''
    ).toUpperCase();

    if (!/^[A-Z]{3}$/.test(selectedCurrency)) return;

    lastDomRefresh = now;

    safeCall(() => {
      const selectedOption = document.querySelector(`.buckscc-select-options li[rel="${selectedCurrency}"]`);
      if (selectedOption) {
        selectedOption.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        return;
      }

      const nativeSelector = document.querySelector('#bucksSelector');
      if (nativeSelector) {
        nativeSelector.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  };

  const runBucks = ({ allowRerender = false } = {}) => {
    if (!hasBucks()) return;

    wrapFreeShippingPrices();
    markCartMoneyForConversion();

    let calledBucksApi = false;

    safeCall(() => {
      if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') {
        window.bucksCC.reConvert();
        calledBucksApi = true;
      }
    });

    if (allowRerender && !lastRunUsedRerender) {
      lastRunUsedRerender = true;
      safeCall(() => {
        if (window.bucksCC && typeof window.bucksCC.rerender === 'function') {
          window.bucksCC.rerender();
          calledBucksApi = true;
        }
      });
      window.setTimeout(() => {
        lastRunUsedRerender = false;
      }, 1000);
    }

    if (!calledBucksApi) {
      triggerBucksDomRefresh();
    }

    startObserver();
  };

  const scheduleRun = (options = {}) => {
    if (!hasBucks()) return;

    window.clearTimeout(scheduledRun);
    scheduledRun = window.setTimeout(() => runBucks(options), options.delay || 100);
  };

  const startObserver = () => {
    if (observerStarted || !hasBucks()) return;

    observerStarted = true;
    observer = new MutationObserver((mutations) => {
      const shouldRun = mutations.some((mutation) => {
        const targetElement = mutation.target.nodeType === Node.ELEMENT_NODE
          ? mutation.target
          : mutation.target.parentElement;

        return (
          (targetElement && targetElement.closest && targetElement.closest(WATCH_SELECTOR))
          || Array.from(mutation.addedNodes).some((node) => (
            node.nodeType === Node.ELEMENT_NODE
            && (node.matches(WATCH_SELECTOR) || node.querySelector(WATCH_SELECTOR))
          ))
        );
      });

      if (shouldRun) {
        scheduleRun({ allowRerender: true, delay: 150 });
      }
    });

    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
  };

  const scheduleInitialRuns = () => {
    if (!hasBucks()) return;

    [250, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => runBucks({ allowRerender: delay === 1000 }), delay);
    });
  };

  const waitForBucks = () => {
    if (hasBucks()) {
      scheduleInitialRuns();
      return;
    }

    bucksReadyPolls += 1;
    if (bucksReadyPolls <= 40) {
      window.setTimeout(waitForBucks, 250);
    }
  };

  CART_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, () => scheduleRun({ allowRerender: true }));
  });

  SEARCH_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, () => {
      scheduleRun({ allowRerender: true, delay: 50 });
      window.setTimeout(() => scheduleRun({ delay: 250 }), 250);
    });
  });

  window.addEventListener('BUCKSCC-LOADED', scheduleInitialRuns);
  document.addEventListener('BUCKS_SELECTED_CURRENCY', () => scheduleRun());

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForBucks);
  } else {
    waitForBucks();
  }

  window.addEventListener('pagehide', () => {
    if (observer) observer.disconnect();
  });
})();
